/**
 * One event row, said in words.
 *
 * **This lives here rather than inside `ActivityPage` because it has two readers.** C7 renders
 * the whole log and C1's Overview card renders its first few rows; before this module existed
 * the Overview printed `event.event_type` raw — `hierarchy_node_created`, in every language.
 * A sentence written once and imported twice is the only shape in which those two screens
 * cannot drift apart again.
 *
 * **The names are the backend's names, spelled the backend's way.** `EVENT_TYPES` is a
 * deliberate duplicate of the event vocabulary emitted by the `activity.record` calls under
 * `backend/src/kalqa/domains/`
 * (ADR-0010's forced-conversation pattern, retro rule 12) — see the TWIN note on the set. The
 * first four entries of this list were wrong for a whole wave (`pin_added` for `pin_created`,
 * `member_*` for `project_member_*`), so every one of those rows fell through to the raw-name
 * fallback while hand-written copy for it sat unused in all three catalogues. If you add an
 * event type to a service, add it here and give it a sentence in `catalogs/{en,sk,de}/activity.ts`.
 */

import type { MessageKeys, Translate } from '@/lib/i18n';

import type { ActivityEvent } from './api';

type ActivityKey = MessageKeys['activity'];

/**
 * The event types we have written a sentence for.
 *
 * A lookup against the catalogue rather than a template built from the type name: the keys are
 * a closed set the i18n budget checks, and a computed key is a key no test can see go missing.
 *
 * TWIN: the emitters are `activity.record(event_type=…)` calls across
 * `backend/src/kalqa/domains/`. Every name below is spelled exactly as the backend emits it —
 * `pin_created`, not `pin_added`; `project_member_added`, not `member_added`. A name that
 * disagrees with its emitter is invisible: the row still renders, as its own raw type.
 */
export const EVENT_TYPES: readonly string[] = [
  'project_created',
  'project_updated',
  'project_archived',
  'hierarchy_seeded',
  'hierarchy_generated',
  'hierarchy_duplicated',
  'hierarchy_grouped',
  'hierarchy_node_created',
  'hierarchy_node_updated',
  'hierarchy_node_removed',
  'hierarchy_reordered',
  'drawing_slot_created',
  'version_published',
  'version_rolled_back',
  'comment_added',
  'comment_resolved',
  'comment_audience_changed',
  'pin_created',
  'pin_resolved',
  'review_requested',
  'review_decided',
  'review_cancelled',
  'project_member_added',
  'project_member_removed',
  'project_member_role_changed',
];

/** The same vocabulary as a membership test, which is what every row does with it. */
export const KNOWN: ReadonlySet<string> = new Set(EVENT_TYPES);

/**
 * The kinds of thing an event on a project can be *about* — what C9's entity filter offers.
 *
 * A subset of the `entity_type` values the backend emits, and deliberately so: the values left
 * out (`workspace`, `user_account`, `auth_session`, `invitation`, …) belong to events with no
 * `project_id`, so a project's log can never hold one and offering it would be a filter that
 * always answers nothing. The order is the tree's: the project, then what is inside it, then
 * what people said about it.
 *
 * TWIN: the `entity_type=` arguments of `activity.record` calls under
 * `backend/src/kalqa/domains/`, and the `entity.` keys of `catalogs/{en,sk,de}/activity.ts`.
 * A value here without a sentence there does not fail the build — it renders as its own raw
 * name, the same honest fallback an unknown event type gets.
 */
export const ENTITY_TYPES: readonly string[] = [
  'project',
  'hierarchy_node',
  'drawing_slot',
  'version',
  'review',
  'comment',
  'pin',
  'project_membership',
];

/**
 * The predicate half of a row: `{actor} {this}`.
 *
 * An unknown event type prints its own name. New event types land in this repo faster than
 * copy for them does, and a row that renders as blank is a row that looks like a bug. The raw
 * type is ugly on purpose: it reads as "we have not written this sentence yet" rather than as
 * "nothing happened".
 */
export function eventSentence(t: Translate<'activity'>, event: ActivityEvent): string {
  // `hierarchy_generated` is the one sentence that takes a number, and the payload is the
  // only place it exists. An absent count renders the sentence with a zero rather than
  // failing the whole row.
  const count = event.payload?.node_count;
  return sentence(t, event.event_type, typeof count === 'number' ? count : 0);
}

/**
 * The same sentence for a bare type name, which is what C9's event-type picker lists.
 *
 * The count is zero because a picker names the *kind* of thing rather than one occurrence of
 * it, and there is no row to read one from. It is the only caller that can honestly pass one.
 */
export function eventLabel(t: Translate<'activity'>, eventType: string): string {
  return sentence(t, eventType, 0);
}

function sentence(t: Translate<'activity'>, eventType: string, count: number): string {
  if (!KNOWN.has(eventType)) {
    return t('event.unknown', { type: eventType });
  }
  return t(`event.${eventType}` as ActivityKey, { count });
}
